/**
 * @file Components/Sections/Packages.jsx
 * @description Travel packages section displaying pricing tiers
 * with features, highlights, and booking call-to-action buttons.
 */

import React from 'react';
import PropTypes from 'prop-types';

const Packages = ({ packages = [], title = "Our Packages" }) => {
  if (!packages || packages.length === 0) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {title}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No packages available at the moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {title}
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Discover our carefully curated travel packages
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              {pkg.image && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {pkg.title}
                </h3>
                
                {pkg.description && (
                  <p className="text-gray-600 mb-4">
                    {pkg.description}
                  </p>
                )}

                <div className="flex items-center justify-between mb-4">
                  {pkg.duration && (
                    <span className="text-sm text-gray-500">
                      {pkg.duration}
                    </span>
                  )}
                  {pkg.price && (
                    <span className="text-lg font-bold text-blue-600">
                      ₹{pkg.price}
                    </span>
                  )}
                </div>

                {pkg.features && pkg.features.length > 0 && (
                  <ul className="text-sm text-gray-600 mb-4">
                    {pkg.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center mb-1">
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}

                <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors duration-200">
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

Packages.propTypes = {
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      image: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      duration: PropTypes.string,
      features: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  title: PropTypes.string
};

export default Packages;
